import { fetchAuthSession } from "@aws-amplify/auth";
import {
    type User,
    type CreateUserRequest,
    type UpdateUserGroupsRequest,
    type UpdateUserProfileRequest,
    type ListUsersResponse,
    type UpdateUserGroupsResponse,
    type UpdateUserProfileResponse,
    type CreateUserResponse,
    type EnableUserResponse,
    type DisableUserResponse,
} from "../../amplify/api/types";
import { get, post, put, del } from "aws-amplify/api";

// API name as configured in Amplify
const API_NAME = "User Management API";
const API_PATH = "/users";

/**
 * List all users
 */
export async function listUsers(): Promise<User[]> {
    // Get the current auth session to include tokens
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    const response = await get({
        apiName: API_NAME,
        path: API_PATH,
        options: {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        },
    }).response;

    const data = (await response.body.json()) as unknown as ListUsersResponse;
    return data.users;
}

/**
 * Get a user by username
 */
export async function getUser(username: string): Promise<User> {
    // Get the current auth session to include tokens
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    const response = await get({
        apiName: API_NAME,
        path: `${API_PATH}/${username}`,
        options: {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        },
    }).response;

    const data = (await response.body.json()) as unknown as { user: User };
    return data.user;
}

/**
 * Create a new user
 */
export async function createUser(userData: CreateUserRequest): Promise<User> {
    // Get the current auth session to include tokens
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    const response = await post({
        apiName: API_NAME,
        path: API_PATH,
        options: {
            body: userData as Record<string, any>,

            headers: {
                Authorization: `Bearer ${token}`,
            },
        },
    }).response;

    const data = (await response.body.json()) as unknown as CreateUserResponse;
    return data.user;
}

/**
 * Update user group memberships
 */
export async function updateUserGroups(
    updateData: UpdateUserGroupsRequest
): Promise<User> {
    // Get the current auth session to include tokens
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    const response = await put({
        apiName: API_NAME,
        path: `${API_PATH}/${updateData.username}/groups`,
        options: {
            body: {
                username: updateData.username,
                addToGroups: updateData.addToGroups || [],
                removeFromGroups: updateData.removeFromGroups || [],
            },
            headers: {
                Authorization: `Bearer ${token}`,
            },
        },
    }).response;

    const data =
        (await response.body.json()) as unknown as UpdateUserGroupsResponse;
    return data.user;
}

/**
 * Update user profile (given name, family name, email)
 */
export async function updateUserProfile(
    updateData: UpdateUserProfileRequest
): Promise<User> {
    const { givenName, familyName, email } = updateData;
    // Get the current auth session to include tokens
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    const body: Record<string, string> = {};
    if (givenName !== undefined) body.givenName = givenName;
    if (familyName !== undefined) body.familyName = familyName;
    if (email) body.email = email;

    const response = await put({
        apiName: API_NAME,
        path: `${API_PATH}/${updateData.username}/profile`,
        options: {
            body: body,
            headers: {
                Authorization: `Bearer ${token}`,
            },
        },
    }).response;

    const data =
        (await response.body.json()) as unknown as UpdateUserProfileResponse;
    return data.user;
}

/**
 * Add a user to groups
 */
export async function addUserToGroups(
    username: string,
    groups: string[]
): Promise<User> {
    return updateUserGroups({
        username,
        addToGroups: groups,
    });
}

/**
 * Remove a user from groups
 */
export async function removeUserFromGroups(
    username: string,
    groups: string[]
): Promise<User> {
    return updateUserGroups({
        username,
        removeFromGroups: groups,
    });
}

/**
 * Check if the current user is an admin (in the wheel group)
 */
export async function isCurrentUserAdmin(): Promise<boolean> {
    try {
        const { tokens } = await fetchAuthSession();
        const payload = tokens?.idToken?.payload;
        const groups = payload?.["cognito:groups"] || [];

        return Array.isArray(groups)
            ? groups.includes("wheel")
            : typeof groups === "string"
              ? groups.split(",").includes("wheel")
              : false;
    } catch (error) {
        console.error("Error checking admin status:", error);
        return false;
    }
}
/**
 * Enable a user
 */
export async function enableUser(username: string): Promise<User> {
    // Get the current auth session to include tokens
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    const response = await post({
        apiName: API_NAME,
        path: `${API_PATH}/${username}/enable`,
        options: {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        },
    }).response;

    const data = (await response.body.json()) as unknown as EnableUserResponse;
    return data.user;
}

/**
 * Disable a user
 */
export async function disableUser(username: string): Promise<User> {
    // Get the current auth session to include tokens
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    const response = await post({
        apiName: API_NAME,
        path: `${API_PATH}/${username}/disable`,
        options: {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        },
    }).response;

    const data = (await response.body.json()) as unknown as DisableUserResponse;
    return data.user;
}

/**
 * Delete a user
 */
export async function deleteUser(username: string): Promise<void> {
    // Get the current auth session to include tokens
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    await del({
        apiName: API_NAME,
        path: `${API_PATH}/${username}`,
        options: {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        },
    }).response;

    return;
}
